import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Eye, Award, Globe2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Overline from "@/components/Overline";
import { COMPANY, INDUSTRIES } from "@/data/site";

export default function About() {
  return (
    <div data-testid="about-page" className="border-b border-slate-200">
      <section className="relative overflow-hidden">
        <div className="tech-grid tech-grid-fade absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
          <Overline>About Us</Overline>
          <h1 className="font-heading mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            A Dubai-built trading house for global technology supply.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
            {COMPANY.legalName} operates from Dubai — one of the world&apos;s most
            connected trading hubs — to deliver IT hardware, electronic
            components and infrastructure solutions to businesses across the
            Middle East, Africa, Asia and beyond.
          </p>
        </div>
      </section>

      <section className="border-t border-slate-200">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-5">
            <Overline>Background</Overline>
            <h2 className="font-heading mt-5 text-3xl font-semibold text-slate-900 sm:text-4xl">
              Company background.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-base leading-relaxed text-slate-700">
            <p>
              GTECGLOBAL COMPUTERS – FZCO is a technology trading and solutions
              company headquartered at IFZA Business Park, Dubai. We supply
              computers, IT hardware, networking equipment, semiconductors and
              electronic components to corporate, government and reseller
              clients globally.
            </p>
            <p>
              Our position in Dubai gives us unmatched logistics reach — between
              East and West, with same-day access to Jebel Ali Port and major
              global airfreight routes. Combined with deep manufacturer and
              distribution relationships, we can deliver competitive pricing and
              fast turnaround on both single SKUs and large project rollouts.
            </p>
            <p>
              We work as a long-term sourcing partner rather than a one-shot
              supplier — building dedicated commercial relationships with each
              of our customers.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card
              icon={Target}
              overline="Mission"
              title="Reliable technology, delivered globally."
              body="To be the most dependable B2B technology trading partner for businesses sourcing IT hardware and electronic components from Dubai — focused on quality, transparency and on-time delivery."
            />
            <Card
              icon={Eye}
              overline="Vision"
              title="A connected, trusted global supply network."
              body="To build a globally trusted network that connects manufacturers, distributors and businesses — making world-class technology accessible at competitive prices, with the service standards of a long-term partner."
            />
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-5">
            <Overline>Dubai Presence</Overline>
            <h2 className="font-heading mt-5 text-3xl font-semibold text-slate-900 sm:text-4xl">
              Strategically based in IFZA, Dubai.
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-500">
              The International Free Zone Authority (IFZA) in Dubai is one of
              the UAE&apos;s most reputed business hubs — giving us the regulatory
              framework, logistics access and global connectivity to operate at
              scale.
            </p>
            <div className="mt-8 rounded-lg border border-slate-200 bg-slate-50 p-6">
              <Globe2 className="h-5 w-5 text-cyan-700" />
              <div className="mt-3 font-heading text-lg text-slate-900">
                {COMPANY.legalName}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {COMPANY.address.line1}
                <br />
                {COMPANY.address.line2}
                <br />
                {COMPANY.address.city}, {COMPANY.address.country}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative aspect-[16/11] overflow-hidden rounded-2xl border border-slate-200">
              <img
                src="https://images.pexels.com/photos/5087165/pexels-photo-5087165.jpeg?auto=compress&cs=tinysrgb&w=1400"
                alt="Dubai skyline"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/20 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Overline>Industries we serve</Overline>
              <h2 className="font-heading mt-5 text-3xl font-semibold text-slate-900 sm:text-4xl">
                Customers across regulated, technical & enterprise sectors.
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-slate-500">
                Whether you&apos;re rolling out branch IT, building a data centre or
                sourcing components for OEM manufacturing — we serve the
                industries that take technology supply seriously.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-100 sm:grid-cols-2">
                {INDUSTRIES.map((ind, idx) => (
                  <li
                    key={ind}
                    data-testid={`industry-${idx}`}
                    className="flex items-center gap-3 bg-white p-5"
                  >
                    <Award className="h-4 w-4 text-cyan-600" />
                    <span className="text-sm text-slate-700">{ind}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-14 flex justify-start">
            <Link to="/contact" data-testid="about-cta-contact">
              <Button
                data-testid="about-cta-contact-btn"
                className="rounded-md bg-cyan-600 px-6 py-5 text-base font-medium text-white hover:bg-cyan-700"
              >
                Start a conversation
                <ArrowUpRight className="ml-1.5 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Card({ icon: Icon, overline, title, body }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-white p-8">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-cyan-100/70 blur-3xl" aria-hidden="true" />
      <div className="relative">
        <Icon className="h-5 w-5 text-cyan-700" />
        <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-600">
          {overline}
        </div>
        <h3 className="font-heading mt-3 text-2xl font-medium text-slate-900">
          {title}
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-slate-500">{body}</p>
      </div>
    </div>
  );
}
